import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/images/logo.webp';

interface SplashScreenProps {
  /** Minimum time to show splash (in ms). Max enforced at 2000ms */
  minDuration?: number;
  /** External loading state - splash hides when this becomes false AND minDuration passed */
  isLoading?: boolean;
  /** Callback when splash finishes */
  onComplete?: () => void;
}

/**
 * Premium Splash Screen Component with Logo Animation
 * 
 * Features:
 * - YIM Burundi logo with elegant pulse animation
 * - Transparent frosted glass background (backdrop-filter blur)
 * - Smooth Framer Motion exit animation
 * - Non-blocking (max 2 second duration)
 */
export function SplashScreen({ 
  minDuration = 1500, 
  isLoading = false,
  onComplete 
}: SplashScreenProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);

  // Enforce max duration of 2 seconds
  const effectiveDuration = Math.min(minDuration, 2000);

  useEffect(() => {
    // Animate progress bar
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        // Smooth easing for progress
        const remaining = 100 - prev;
        const increment = Math.max(remaining * 0.1, 1);
        return Math.min(prev + increment, 100);
      });
    }, effectiveDuration / 50);

    // Timer for minimum duration
    const timer = setTimeout(() => {
      if (!isLoading) {
        setIsVisible(false);
        onComplete?.();
      }
    }, effectiveDuration);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(timer);
    };
  }, [effectiveDuration, isLoading, onComplete]);

  // Watch for loading completion after min duration
  useEffect(() => {
    if (!isLoading && progress >= 95) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isLoading, progress, onComplete]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            scale: 1.05,
            transition: { 
              duration: 0.5, 
              ease: [0.4, 0, 0.2, 1] 
            }
          }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden bg-black/60 backdrop-blur-md"
        >
          {/* Logo with pulse animation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="relative"
          >
            {/* Glow effect behind logo */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(251, 191, 36, 0.3) 0%, transparent 70%)',
                filter: 'blur(30px)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            
            {/* Logo */}
            <motion.img
              src={logo}
              alt="YIM Burundi"
              width={144}
              height={144}
              loading="eager"
              decoding="async"
              className="w-28 h-28 sm:w-36 sm:h-36 object-contain relative z-10 drop-shadow-2xl"
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </motion.div>

          {/* Brand name */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-6 text-center"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wide">
              YIM <span className="text-amber-400">Burundi</span>
            </h1>
            <p className="text-white/60 text-sm mt-1 font-light tracking-wider">
              Youth Impact Mission
            </p>
          </motion.div>

          {/* Progress bar at bottom */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden bg-white/10"
          >
            <motion.div
              className="h-full"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #FBBF24 0%, #F59E0B 50%, #FBBF24 100%)',
                backgroundSize: '200% 100%',
                boxShadow: '0 0 20px rgba(251, 191, 36, 0.6)',
              }}
              animate={{
                backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SplashScreen;
